import type { ReferenceData } from "~/modules/reference_data/types";
import { CopySlugBadge } from "~/modules/catalog/components/CopySlugBadge";
import { CatalogStatChip } from "~/modules/catalog/components/CatalogStatChip";
import { SectionHeader } from "~/components/SectionHeader";
import { ReferenceAssets } from "./ReferenceAssets";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { DEFAULT_ASSET_LIST_QUERY } from "~/modules/assets/consts";
import { AssetColumns } from "~/modules/assets/types";
import {
    ApiQuery,
    ApiQueryEndpoints,
    API_QUERY_BASE_URLS,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import { VisualizationApiToolsButton } from "~/modules/visualization/components/VisualizationApiToolsButton";
import { getCatalogCoveragePercent } from "../../lib/getCatalogCoveragePercent";

// // // //

interface Props {
    referenceData: ReferenceData;
    totalAssetCount?: number;
}

export function ReferenceDataIndividualPage({
    referenceData,
    totalAssetCount,
}: Props) {
    const assetsQuery: ApiQuery = {
        ...DEFAULT_ASSET_LIST_QUERY,
        filter: {
            operator: FilterOperators.And,
            filters: [
                {
                    field: AssetColumns.tokenCount,
                    operator: FilterOperators.GreaterThan,
                    value: 0,
                },
                {
                    field: referenceData.asset_table_column_name,
                    operator: FilterOperators.IsNotNull,
                    value: null,
                },
            ],
        },
        sort: {
            field: AssetColumns.name,
            direction: SortDirections.asc,
        },
        pagination: {
            page: 1,
            perPage: 25,
        },
    };

    return (
        <>
            <div className="flex items-center gap-2 flex-wrap mb-5">
                <CatalogStatChip label="Category">
                    {referenceData.category}
                </CatalogStatChip>
                <CatalogStatChip label="Coverage">
                    {referenceData.asset_count.toLocaleString()}
                    {totalAssetCount !== undefined && (
                        <>
                            {" "}
                            / {totalAssetCount.toLocaleString()}
                            <span className="ml-1.5 text-primary">
                                {getCatalogCoveragePercent({
                                    totalAssetCount,
                                    assetCount: referenceData.asset_count,
                                })}
                            </span>
                        </>
                    )}
                </CatalogStatChip>
                <CopySlugBadge slug={referenceData.slug} />
            </div>

            <SectionHeader label="Assets">
                <div className="flex justify-end items-center gap-2">
                    <VisualizationApiToolsButton
                        size="xs"
                        query={assetsQuery}
                        apiDocsBaseUrl={
                            API_QUERY_BASE_URLS[ApiQueryEndpoints.AssetV4]
                        }
                    />
                    <DataExportProvider>
                        {({ isLoading, exportCsv }) => (
                            <DownloadButton
                                disabled={isLoading}
                                onClick={() => {
                                    exportCsv({
                                        exportTableName:
                                            ExportTableNames.assets_v4,
                                        query: assetsQuery,
                                    });
                                }}
                            />
                        )}
                    </DataExportProvider>
                </div>
            </SectionHeader>

            <ReferenceAssets reference={referenceData} />
        </>
    );
}
